import React, { FunctionComponent } from 'react';

import { Grid } from 'semantic-ui-react';
import { ClassProp } from 'helpers/types';
import { subYears } from 'date-fns';
import withStyles from 'react-jss';
import { map, pipe } from 'ramda';

import { DiscreteColorLegend } from 'react-vis';

import { getTickFormat, Timeslice } from 'scenes/Report/Report.helper';
import { VenueType } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import {
  MerchantTransaction,
  transactionsTransformer,
  normalizeTotals,
  DateRange,
  checkForYearsWithDifferentWeeks,
  Scale,
  reportColors,
} from './YearOverYear.helper';

import styles from './YearOverYear.styles';
import PlotByVenueType from './PlotByVenueType';
import PlotByTotals from './PlotByTotals';

type Props = {
  classes: ClassProp<typeof styles>;
  title: string;
  transactions: MerchantTransaction[];
  filterByVenueType: boolean;
  timeslice: Timeslice;
  years: string[];
  dateRange: DateRange;
  labelFormat: string;
  transformer: (
    startDate: Date,
    endDate: Date,
    venue?: VenueType | undefined,
  ) => (x0: MerchantTransaction[]) => Scale[];
  height: number;
};

const tickFormat = (timeslice: Timeslice, scales: Scale[] = []) => (
  value: string,
  index: number,
): JSX.Element => {
  const { top, bottom } = getTickFormat(timeslice, value, scales[index]);

  return (
    <tspan>
      <tspan x="0" dy="1em">
        {top}
      </tspan>

      <br />

      <tspan x="0" dy="1em">
        {bottom}
      </tspan>
    </tspan>
  );
};

const legends = (
  classes: ClassProp<typeof styles>,
  filterByVenueType: boolean,
  years: string[],
) => {
  let items = years.map((year, index) => {
    return {
      title: year,
      color: reportColors[index],
    };
  });
  if (filterByVenueType) {
    items = [];
    years.forEach((year, index) => {
      items.push({
        title: `${year} - Online`,
        color: reportColors[index],
      });
      items.push({
        title: `${year} - In Store`,
        color: reportColors[index + 2],
      });
    });
  }
  return (
    <Grid.Row centered>
      <DiscreteColorLegend
        className={classes.legend}
        orientation="horizontal"
        items={items}
      />
    </Grid.Row>
  );
};

const PlotYearOverYear: FunctionComponent<Props> = ({
  transactions,
  title,
  filterByVenueType,
  timeslice,
  years,
  classes,
  dateRange,
  labelFormat,
  transformer,
  height,
}: Props) => {
  const { from, to } = dateRange;
  const filterTotalsByVenue = (venue?: VenueType) =>
    pipe(
      map(({ from, to }: DateRange) =>
        transformer(from, to, venue)(transactions),
      ),
      map((totals) => normalizeTotals(timeslice)(totals)),
    )([
      { from, to },
      { from: subYears(from, 1), to: subYears(to, 1) },
    ]);

  let [
    totalsByYear,
    totalsNearbyByYear,
    totalsOnlineByYear,
  ] = map(filterTotalsByVenue, [undefined, VenueType.NEARBY, VenueType.ONLINE]);

  totalsByYear = checkForYearsWithDifferentWeeks(
    totalsByYear[0],
    totalsByYear[1],
  );

  totalsNearbyByYear = checkForYearsWithDifferentWeeks(
    totalsNearbyByYear[1],
    totalsNearbyByYear[0],
  );

  totalsOnlineByYear = checkForYearsWithDifferentWeeks(
    totalsOnlineByYear[1],
    totalsOnlineByYear[0],
  );

  const totalTransactions = transactionsTransformer()(transactions);

  return (
    <>
      <Grid.Row centered>
        <h4>{filterByVenueType ? title + 'by Venue Type' : title}</h4>
      </Grid.Row>
      <Grid.Row centered>
        <Grid.Column textAlign="center" width={14}></Grid.Column>
        {filterByVenueType ? (
          <PlotByVenueType
            timeslice={timeslice}
            totalTransactions={totalTransactions}
            totalsNearbyByYear={totalsNearbyByYear}
            totalsOnlineByYear={totalsOnlineByYear}
            labelFormat={labelFormat}
            height={height}
            tickFormat={tickFormat}
          />
        ) : (
          <PlotByTotals
            timeslice={timeslice}
            totalTransactions={totalTransactions}
            labelFormat={labelFormat}
            height={height}
            totalsByYear={totalsByYear}
            tickFormat={tickFormat}
          />
        )}
      </Grid.Row>
      {legends(classes, filterByVenueType, years)}
    </>
  );
};

export default withStyles(styles)(PlotYearOverYear);
